const { $ } = window;
const APPLE_PAY_VERSION = 14;

/**
 * Log une erreur Payplug dans la console
 * @param {Error|string} error - L'erreur à logger
 * @param {Object} context - Contexte additionnel
 */
function capturePayplugError(error, context = {}) {
    const errorObj = error instanceof Error ? error : new Error(error);
    console.error('[PAYPLUG]', errorObj.message, context);
}

/**
 * Récupère le domaine parent via le paramètre d'URL ou sessionStorage
 * @returns {string|null} Le hostname du domaine, ou null si iframe sans parentDomain
 */
function getParentDomain() {
    const isInIframe = window !== window.top;

    const urlParams = new URLSearchParams(window.location.search);
    let parentDomain = urlParams.get('parentDomain');

    if (parentDomain) {
        // Stocker pour les pages suivantes
        sessionStorage.setItem('yp_parentDomain', parentDomain);
        return parentDomain;
    }

    if (isInIframe) {
        parentDomain = sessionStorage.getItem('yp_parentDomain');
        if (parentDomain) {
            return parentDomain;
        }
        // Dans une iframe mais sans parentDomain - blocage d'Apple Pay
        return null;
    } else {
        const storedDomain = sessionStorage.getItem('yp_parentDomain');
        if (storedDomain) {
            sessionStorage.removeItem('yp_parentDomain');
        }
    }

    return window.location.hostname;
}

function isApplePayAllowedForDomain(domain) {
    if (domain === null) {
        return false;
    }

    const allowedDomains = [
        'yurplan.com',
        'stage.yurplan.com',
        'yp.events',
        'stage.yp.events',
        'billetterie.yurplan.com',
        'billetterie.stage.yurplan.com',
    ];

    return allowedDomains.includes(domain);
}

window.YP.PayplugPaymentManager = function PayplugPaymentManager() {
    const paymentForm = $('#payment-form');
    const freePaymentForm = $('#free-payment-form');
    const paymentTypeChoice = $('input[name="paymentForm[payment_method_choice]"]');
    let alreadySubmitted = false;
    let payplugInstance = null;

    const PAYMENT_METHOD_CB = 'CREDIT_CARD';
    const PAYMENT_METHOD_DELAYED = 'DELAYED';
    const PAYMENT_METHOD_APPLE_PAY = 'APPLE_PAY';
    const DEFAULT_SCHEME_NAME = 'CARTE_BANCAIRE';

    const TRANSLATIONS = {
        fr: {
            cardHolder: 'NOM PRÉNOM',
            cvv: '3 chiffres',
            expiration: 'MM/AA'
        },
        en: {
            cardHolder: 'FULL NAME',
            cvv: '3 digits',
            expiration: 'MM/YY'
        }
    };

    const getLocale = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const cultureFromUrl = urlParams.get('culture');
        if (cultureFromUrl) {
            return cultureFromUrl === 'fr' ? 'fr' : 'en';
        }

        if (window.YurPlanWidgets && window.YurPlanWidgets.ypFrames) {
            // Try to extract culture from ypFrames src attribute
            const frames = window.YurPlanWidgets.ypFrames;
            if (frames.length > 0) {
                try {
                    const frameSrc = frames[0].src;
                    const frameUrl = new URL(frameSrc);
                    const frameCulture = frameUrl.searchParams.get('culture');
                    if (frameCulture) {
                        return frameCulture === 'fr' ? 'fr' : 'en';
                    }
                } catch (e) {
                    // Ignore parsing errors
                }
            }
        }

        // Default: English
        return 'en';
    };

    return {
        currentSubmitContext: null,
        init() {
            if (paymentForm.length > 0) {
                paymentTypeChoice.first().prop('checked', true);
                paymentTypeChoice.first().trigger('change');
                this.initPaymentForm();
                this.handlePaymentFormSubmit();
            }
            if (freePaymentForm.length > 0) {
                this.initFreePaymentForm();
            }
        },
        initPaymentForm() {
            const isSandbox = $('#payplug_is_sandbox').val() === '1';

            if (typeof window.Payplug === 'undefined') {
                capturePayplugError('Payplug SDK not loaded', { step: 'init' });
                return;
            }

            try {
                payplugInstance = new window.Payplug.IntegratedPayment(isSandbox);

                const locale = getLocale();
                const translations = TRANSLATIONS[locale];

                const defaultFieldOptions = {
                    default: {
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '16px'
                    }
                };

                payplugInstance.cardHolder(document.getElementById('payplug-cardholder-container'), {
                    placeholder: translations.cardHolder,
                    ...defaultFieldOptions
                });
                payplugInstance.cardNumber(document.getElementById('payplug-card-container'), {
                    placeholder: '1234 5678 9012 3456',
                    ...defaultFieldOptions
                });
                payplugInstance.cvv(document.getElementById('payplug-cvv-container'), {
                    placeholder: translations.cvv,
                    ...defaultFieldOptions
                });
                payplugInstance.expiration(document.getElementById('payplug-expiry-container'), {
                    placeholder: translations.expiration,
                    ...defaultFieldOptions
                });
                
                // Récupérer et afficher les schemes disponibles
                this.initSchemes();

                // Initialiser Apple Pay
                this.initApplePay();

                // Configurer le mode d'affichage 3DS en fullscreen (redirection)
                payplugInstance.setDisplayMode3ds(window.Payplug.DisplayMode3ds.REDIRECT);

                // Enregistrer les callbacks une seule fois pour éviter l'accumulation
                const self = this;

                payplugInstance.onValidateForm(async ({ isFormValid }) => {
                    if (!self.currentSubmitContext) {
                        console.error('[PAYPLUG] No submit context available, skip onValidateForm');
                        return;
                    }

                    const { theForm, btn, oldVal } = self.currentSubmitContext;

                    if (!isFormValid) {
                        alreadySubmitted = false;
                        btn.removeClass('c-btn--loading');
                        btn.removeAttr('disabled');
                        btn.text(oldVal);
                        return;
                    }

                    try {
                        const paymentReference = $('#payment_reference').val();

                        const response = await fetch('/create-card-payment', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                                paymentReference: paymentReference
                            })
                        });

                        if (!response.ok) {
                            const errorData = await response.json();
                            throw new Error(errorData.error || 'Failed to create payment');
                        }

                        const data = await response.json();
                        const paymentId = data.paymentId;

                        const selectedSchemeElement = document.querySelector('input[name="payplug_scheme"]:checked');
                        const schemeId = selectedSchemeElement ? parseInt(selectedSchemeElement.value) : 0;

                        payplugInstance.pay(paymentId, schemeId);

                    } catch (error) {
                        capturePayplugError(error, { step: 'payment_creation' });
                        alreadySubmitted = false;
                        btn.removeClass('c-btn--loading');
                        btn.removeAttr('disabled');
                        btn.text(oldVal);
                        alert('Erreur lors de la création du paiement. Veuillez réessayer.');
                    }
                });

                payplugInstance.onCompleted(() => {
                    if (!self.currentSubmitContext) {
                        console.error('[PAYPLUG] No submit context available, skip onCompleted');
                        return;
                    }

                    const { theForm } = self.currentSubmitContext;
                    theForm.submit();
                });
            } catch (error) {
                capturePayplugError(error, { step: 'init_form' });
            }
        },
        initSchemes() {
            const schemes = payplugInstance.getSupportedSchemes();
            const schemeContainer = document.getElementById('payplug-scheme-container-label');

            if (!schemeContainer || !schemes || schemes.length === 0) {
                return;
            }

            let defaultSelected = false;
            schemes
                .filter((scheme) => scheme.id !== 0) // Ignorer le scheme AUTO (id=0)
                .sort((a, b) => {
                    // Mettre CARTE_BANCAIRE en premier
                    if (a.name === DEFAULT_SCHEME_NAME) return -1;
                    if (b.name === DEFAULT_SCHEME_NAME) return 1;
                    return 0;
                })
                .forEach((scheme) => {
                    const label = document.createElement('label');
                    label.className = 'payplug-scheme-option';

                    const radio = document.createElement('input');
                    radio.type = 'radio';
                    radio.name = 'payplug_scheme';
                    radio.value = scheme.id;
                    radio.id = `scheme_${scheme.id}`;

                    if (scheme.name === DEFAULT_SCHEME_NAME && !defaultSelected) {
                        radio.checked = true;
                        defaultSelected = true;
                    }

                    const img = document.createElement('img');
                    img.src = scheme.iconUrl;
                    img.alt = scheme.name;
                    img.title = scheme.title;
                    img.className = 'payplug-scheme-icon';

                    label.appendChild(radio);
                    label.appendChild(img);
                    schemeContainer.appendChild(label);
                }
            );
        },
        initApplePay() {
            const paymentReference = $('#payment_reference').val();
            const applePayButton = document.getElementsByTagName('apple-pay-button')[0];
            const applePayContainer = document.querySelector('[data-payment-method="APPLE_PAY"]');
            const totalAmount = $('#payment_total_amount').val();
            const eventLabel = $('#event_label').val();
            const widget = $('input[name="widget"]').val() || 'false';

            let currentApplePaySession = null;
            let applePayInProgress = false;

            if (!applePayButton || !window.Payplug || !paymentReference) {
                console.warn('[PAYPLUG] Apple Pay button or Payplug SDK or payment reference not found');
                return;
            }

            const hideApplePayElements = () => {
                applePayButton.style.display = 'none';
                if (applePayContainer) {
                    applePayContainer.style.display = 'none';
                }
            };

            const currentDomain = getParentDomain();
            if (!isApplePayAllowedForDomain(currentDomain)) {
                console.warn(`[PAYPLUG] Apple Pay not allowed for domain: ${currentDomain}`);
                hideApplePayElements();
                return;
            }

            // Vérifier si Apple Pay est disponible
            if (!window.ApplePaySession) {
                console.warn('[PAYPLUG] Apple Pay API not available');
                hideApplePayElements();
                return;
            }

            try {
                // Vérifier si les paiements Apple Pay sont possibles
                if (!window.ApplePaySession.canMakePayments()) {
                    hideApplePayElements();
                    return;
                }
            } catch (error) {
                hideApplePayElements();
                return;
            }

            applePayButton.addEventListener('click', () => {
                if (applePayInProgress) {
                    console.warn('[PAYPLUG] Apple Pay already in progress, ignoring click');
                    return;
                }

                if (!ApplePaySession) {
                    return;
                }

                // Abort de la session précédente si elle existe (fix FRONTEND-LEGACY-G)
                if (currentApplePaySession) {
                    try {
                        currentApplePaySession.abort();
                        console.debug('[PAYPLUG] Aborted previous Apple Pay session');
                    } catch (e) {
                        console.warn('[PAYPLUG] Previous Apple Pay session could not be aborted');
                    }
                    currentApplePaySession = null;
                }

                applePayInProgress = true;

                const request = {
                    "countryCode": "FR",
                    "currencyCode": "EUR",
                    "merchantCapabilities": [
                        "supports3DS"
                    ],
                    "supportedNetworks": [
                        "visa",
                        "masterCard"
                    ],
                    "total": {
                        "label": eventLabel,
                        "type": "final",
                        "amount": totalAmount
                    },
                    "applicationData": btoa(JSON.stringify({apple_pay_domain: getParentDomain()}))
                };

                let session;
                try {
                    session = new ApplePaySession(APPLE_PAY_VERSION, request);
                } catch (error) {
                    capturePayplugError(error, { step: 'apple_pay_session_creation', domain: currentDomain });
                    applePayInProgress = false;
                    currentApplePaySession = null;
                    return;
                }
                currentApplePaySession = session;

                session.onvalidatemerchant = async event => {
                    const merchantSession = await validateMerchant();

                    if (!merchantSession) {
                        session.abort();
                        applePayInProgress = false;
                        currentApplePaySession = null;
                        return;
                    }

                    try {
                        session.completeMerchantValidation(merchantSession);
                    } catch (error) {
                        capturePayplugError(error, { step: 'apple_pay_merchant_validation_complete', domain: currentDomain });
                        session.abort();
                        applePayInProgress = false;
                        currentApplePaySession = null;
                    }
                };

                async function validateMerchant() {
                    try {
                        const response = await fetch('/create-apple-payment', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                                paymentReference: paymentReference,
                                domain: getParentDomain()
                            })
                        });

                        if (!response.ok) {
                            const errorData = await response.json();
                            throw new Error(errorData.error || 'Failed to validate merchant');
                        }

                        const data = await response.json();

                        // Paiement déjà encaissé côté Payplug — rediriger vers afterbank
                        if (data.already_paid && data.redirectUrl) {
                            session.abort();
                            applePayInProgress = false;
                            currentApplePaySession = null;

                            if (widget !== 'false') {
                                const parentDomain = getParentDomain();
                                const protocol = window.location.protocol;
                                window.top.location.href = `${protocol}//${parentDomain}${data.redirectUrl}`;
                            } else {
                                window.location.href = data.redirectUrl;
                            }
                            return null;
                        }

                        let merchantSession = data.merchant_session;
                        if (typeof merchantSession === 'string') {
                            try {
                                merchantSession = JSON.parse(merchantSession);
                            } catch (e) {
                                throw new Error('Invalid merchant session format');
                            }
                        }

                        return merchantSession;
                    } catch (error) {
                        capturePayplugError(error, { step: 'apple_pay_merchant_validation', domain: getParentDomain() });
                    }
                }

                session.onpaymentauthorized = async (event) => {
                    try {
                        const response = await fetch('/update-apple-payment', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                                paymentReference: paymentReference,
                                applePayToken: event.payment.token,
                                widget: widget
                            })
                        });

                        const data = await response.json();

                        if (response.ok && data.success) {
                            session.completePayment(ApplePaySession.STATUS_SUCCESS);

                            // Redirection vers l'afterbank
                            setTimeout(() => {
                                // Si widget, construire l'URL avec parentDomain et rediriger le parent
                                if (widget !== 'false') {
                                    const parentDomain = getParentDomain();
                                    const protocol = window.location.protocol;
                                    const fullUrl = `${protocol}//${parentDomain}${data.redirectUrl}`;

                                    // Rediriger la page parente, pas l'iframe
                                    window.top.location.href = fullUrl;
                                } else {
                                    // Pas de widget, redirection normale
                                    window.location.href = data.redirectUrl;
                                }
                            }, 500);

                        } else {
                            session.completePayment(ApplePaySession.STATUS_FAILURE);
                            applePayInProgress = false;
                            currentApplePaySession = null;
                            alreadySubmitted = false;
                        }

                    } catch (error) {
                        session.completePayment(ApplePaySession.STATUS_FAILURE);
                        applePayInProgress = false;
                        currentApplePaySession = null;
                        alreadySubmitted = false;
                        capturePayplugError(error, { step: 'apple_pay_authorization', domain: getParentDomain() });
                    }
                };

                session.oncancel = async (event) => {
                    applePayInProgress = false;
                    currentApplePaySession = null;
                    alreadySubmitted = false;
                };

                session.begin();
            });
        },
        handlePaymentFormSubmit() {
            const self = this;
            paymentForm.on('submit', function handlePaymentFormSubmit(e) {
                const theForm = this;
                e.preventDefault();

                if (alreadySubmitted) {
                    return e.preventDefault();
                }
                alreadySubmitted = true;
                const btn = $(this).find('.c-btn');
                const oldVal = btn.val();
                btn.addClass('c-btn--loading');
                btn.attr('disabled', 'disabled');

                switch (paymentTypeChoice.filter(':checked').val()) {
                    case PAYMENT_METHOD_CB:
                        // Stocker le contexte pour les callbacks Payplug
                        self.currentSubmitContext = { theForm, btn, oldVal };
                        self.handlePayplugPayment();
                        break;

                    case PAYMENT_METHOD_APPLE_PAY:
                        // Pour Apple Pay, empêcher la soumission classique
                        // Le flux Apple Pay gérera la soumission via onpaymentauthorized
                        alreadySubmitted = false;
                        btn.removeClass('c-btn--loading');
                        btn.removeAttr('disabled');
                        return false;

                    case PAYMENT_METHOD_DELAYED:
                        theForm.submit();
                        break;
                    default:
                        alert('Moyen de paiement non autorisé');
                        break;
                }
                return false;
            });
        },
        async handlePayplugPayment() {
            if (!payplugInstance) {
                console.error('[PAYPLUG] Instance not initialized');
                const { btn } = this.currentSubmitContext;
                alreadySubmitted = false;
                btn.removeClass('c-btn--loading');
                btn.removeAttr('disabled');
                return;
            }

            // Déclencher la validation du formulaire
            // Le callback onValidateForm enregistré dans initPaymentForm sera appelé
            payplugInstance.validateForm();
        },
        initFreePaymentForm() {
            if (freePaymentForm.length > 0) {
                const confirmButton = freePaymentForm.find('.c-btn');
                confirmButton.on('click', function(e) {
                    if ($(this).prop('disabled')) {
                        e.preventDefault();
                        return false;
                    }
                    $(this).addClass('c-btn--loading');
                    $(this).attr('disabled', 'disabled');
                });
            }
        },
    };
};
